import React, { useState } from 'react';
import { Palette, Shield, Lock, Upload, Camera, CheckCircle } from 'lucide-react';
import { User } from '../../types';
import { api } from '../../services/api';
import styles from './Settings.module.css';

interface SettingsModalProps {
  user: User | null;
  onUpdateUser: (updated: User) => void;
  onLogout: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ user, onUpdateUser, onLogout }) => {
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [bannerUrl, setBannerUrl] = useState(user?.banner_url || '');
  const [pronouns, setPronouns] = useState(user?.pronouns || '');
  const [location, setLocation] = useState(user?.location || '');
  const [website, setWebsite] = useState(user?.website || '');
  const [customStatusText, setCustomStatusText] = useState(user?.custom_status_text || '');
  const [currentTheme, setCurrentTheme] = useState(user?.custom_theme || 'neon-indigo');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [message, setMessage] = useState('');

  if (!user) return null;

  const themes = [
    { id: 'neon-indigo', label: 'Twitter Blue / Indigo', color: '#1d9bf0' },
    { id: 'emerald-aurora', label: 'Emerald Green', color: '#00ba7c' },
    { id: 'violet-cyber', label: 'Violet Purple', color: '#7856ff' },
    { id: 'rose-sunset', label: 'Rose Red', color: '#f4212e' },
    { id: 'discord-dark', label: 'Discord Dark', color: '#5865f2' },
  ];

  const handleSelectTheme = async (themeId: string) => {
    setCurrentTheme(themeId);
    document.documentElement.setAttribute('data-theme', themeId);
  };

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const res = await api.uploadMedia(file);
      setAvatarUrl(res.url);
      const updated = await api.updateProfile({ avatar_url: res.url });
      onUpdateUser(updated.user);
      setMessage('Avatar picture updated!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      alert(err.message || 'Avatar upload failed');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleBannerFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const res = await api.uploadMedia(file);
      setBannerUrl(res.url);
      const updated = await api.updateProfile({ banner_url: res.url });
      onUpdateUser(updated.user);
      setMessage('Banner picture updated!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      alert(err.message || 'Banner upload failed');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const res = await api.updateProfile({
        display_name: displayName,
        bio,
        avatar_url: avatarUrl,
        banner_url: bannerUrl,
        pronouns,
        location,
        website,
        custom_status_text: customStatusText,
        custom_theme: currentTheme,
      });
      onUpdateUser(res.user);
      setMessage('Profile settings saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      alert(err.message || 'Save failed');
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Account & Profile Settings</h2>

      {message && (
        <div style={{ padding: '10px 14px', borderRadius: '8px', background: 'rgba(0, 186, 124, 0.15)', color: '#00ba7c', marginBottom: '16px', fontWeight: '600' }}>
          <CheckCircle size={16} /> {message}
        </div>
      )}

      {/* PC Image File Upload Section */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <Camera size={18} color="var(--accent-primary)" /> Profile Pictures (Upload from PC)
        </div>

        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '14px' }}>
          {/* Avatar Upload */}
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: '700', marginBottom: '8px' }}>Avatar Picture</div>
            <div style={{ position: 'relative', width: '90px', height: '90px' }}>
              <img src={avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-subtle)' }} />
              <label style={{ position: 'absolute', bottom: 0, right: 0, background: 'var(--accent-primary)', color: 'white', borderRadius: '50%', padding: '6px', cursor: 'pointer' }}>
                <Upload size={14} />
                <input type="file" accept="image/*" onChange={handleAvatarFile} style={{ display: 'none' }} />
              </label>
            </div>
            {uploadingAvatar && <div style={{ fontSize: '0.78rem', color: 'var(--accent-primary)', marginTop: '4px' }}>Uploading...</div>}
          </div>

          {/* Banner Upload */}
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: '700', marginBottom: '8px' }}>Header Banner Picture</div>
            <div style={{ position: 'relative', width: '100%', height: '90px', borderRadius: '12px', overflow: 'hidden' }}>
              <img src={bannerUrl} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <label style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.7)', color: 'white', borderRadius: '20px', padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Upload size={14} /> Upload Banner
                <input type="file" accept="image/*" onChange={handleBannerFile} style={{ display: 'none' }} />
              </label>
            </div>
            {uploadingBanner && <div style={{ fontSize: '0.78rem', color: 'var(--accent-primary)', marginTop: '4px' }}>Uploading...</div>}
          </div>
        </div>
      </div>

      {/* Theme Presets */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <Palette size={18} color="var(--accent-primary)" /> Color Theme Presets
        </div>
        <div className={styles.themeGrid}>
          {themes.map((t) => (
            <div
              key={t.id}
              className={`${styles.themeCard} ${currentTheme === t.id ? styles.activeTheme : ''}`}
              onClick={() => handleSelectTheme(t.id)}
            >
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: t.color, margin: '0 auto 8px auto' }} />
              {t.label}
            </div>
          ))}
        </div>
      </div>

      {/* Profile Details Form */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <Shield size={18} color="var(--accent-primary)" /> Profile Info
        </div>
        <div className={styles.formRow}>
          <label className={styles.label}>Display Name</label>
          <input className={styles.input} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className={styles.formRow}>
          <label className={styles.label}>Bio</label>
          <input className={styles.input} value={bio} onChange={(e) => setBio(e.target.value)} />
        </div>
        <div className={styles.formRow}>
          <label className={styles.label}>Pronouns</label>
          <input className={styles.input} placeholder="they/them" value={pronouns} onChange={(e) => setPronouns(e.target.value)} />
        </div>
        <div className={styles.formRow}>
          <label className={styles.label}>Custom Status Text</label>
          <input className={styles.input} placeholder="Building on Cloudflare ⚡" value={customStatusText} onChange={(e) => setCustomStatusText(e.target.value)} />
        </div>
        <div className={styles.formRow}>
          <label className={styles.label}>Location</label>
          <input className={styles.input} value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div className={styles.formRow}>
          <label className={styles.label}>Website</label>
          <input className={styles.input} value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>
        <button className={styles.saveBtn} onClick={handleSaveProfile}>
          Save Profile Changes
        </button>
      </div>

      <div className={styles.section} style={{ borderColor: 'rgba(244, 33, 46, 0.3)' }}>
        <button
          style={{ padding: '10px 20px', borderRadius: '20px', background: '#f4212e', color: '#ffffff', fontWeight: '700' }}
          onClick={onLogout}
        >
          Sign Out of Cordle
        </button>
      </div>
    </div>
  );
};
